// src/contexts/CartContext.jsx
import React, { createContext, useContext, useMemo, useState, useEffect, useRef } from 'react';
import { useQuery as useApolloQuery } from '@apollo/client';
import FETCH_CART_DETAILS from '../api/queries/FetchCartDetails.gql';
import { getExistingCheckout, consoleDebug } from '../util';
import * as storage from '../browserStorage';
import { storageItemName } from '../config';

const CartContext = createContext(null);

export const useCart = () => {
    const context = useContext(CartContext);
    if (!context) {
        throw new Error('useCart must be used within CartProvider');
    }
    return context;
};

// Create a separate context for updating retailerId
export const CartRetailerContext = createContext(null);

export const CartProvider = ({ children }) => {
    // Retailer ID will be set by App component
    const [retailerId, setRetailerId] = useState(null);
    const checkout = getExistingCheckout();
    
    // Track if we've already attempted to fetch to prevent duplicate queries
    const fetchAttemptedRef = useRef(false);
    const lastFetchParamsRef = useRef(null);
    
    // Create a stable key for tracking fetch params
    const fetchParamsKey = useMemo(() => {
        if (!retailerId || !checkout?.checkoutId || checkout?.retailerId !== retailerId) {
            return null;
        }
        return `${checkout.checkoutId}-${retailerId}`;
    }, [retailerId, checkout?.checkoutId, checkout?.retailerId]);
    
    // Determine if we should skip the query
    const shouldSkip = useMemo(() => {
        // Skip if no params or params haven't changed
        if (!fetchParamsKey) return true;
        if (lastFetchParamsRef.current === fetchParamsKey && fetchAttemptedRef.current) return true;
        return false;
    }, [fetchParamsKey]);

    const { data, loading, error, refetch, networkStatus, client } = useApolloQuery(
        FETCH_CART_DETAILS,
        {
            variables: {
                checkoutId: checkout?.checkoutId,
                retailerId: checkout?.retailerId,
            },
            skip: shouldSkip,
            fetchPolicy: 'cache-first',
            nextFetchPolicy: 'cache-first',
            notifyOnNetworkStatusChange: true,
            errorPolicy: 'all',
            onCompleted: (data) => {
                fetchAttemptedRef.current = true;
                lastFetchParamsRef.current = fetchParamsKey;
                consoleDebug('✅ Cart query completed:', {
                    hasCheckout: !!data?.checkout,
                    checkoutId: data?.checkout?.id,
                    itemCount: data?.checkout?.items?.length
                });
            },
            onError: (error) => {
                fetchAttemptedRef.current = true;
                lastFetchParamsRef.current = fetchParamsKey;
                console.error('Cart fetch error:', error);
                
                if (error.graphQLErrors?.some(err => err.message === 'Not Found')) {
                    console.log('Cart not found, clearing storage');
                    storage.removeItem(storageItemName);
                    
                    // Clear the cart from cache
                    if (checkout?.checkoutId) {
                        client.cache.evict({
                            id: client.cache.identify({ 
                                __typename: 'Checkout', 
                                id: checkout.checkoutId 
                            })
                        });
                        client.cache.gc();
                    }
                }
            }
        }
    );

    // Reset fetch attempted when retailerId changes
    useEffect(() => {
        if (retailerId && lastFetchParamsRef.current !== fetchParamsKey) {
            fetchAttemptedRef.current = false;
        }
    }, [retailerId, fetchParamsKey]);

    // Memoize the context value to prevent unnecessary re-renders
    const contextValue = useMemo(() => ({
        cartData: data?.checkout || null,
        isFetched: shouldSkip || (!loading && (networkStatus === 7 || error)),
        isFetching: loading && !shouldSkip,
        error: shouldSkip ? null : error,
        refetch: () => {
            fetchAttemptedRef.current = false;
            return refetch();
        },
        retailerId
    }), [data, loading, error, networkStatus, shouldSkip, refetch, retailerId]);

    return (
        <CartRetailerContext.Provider value={{ setRetailerId }}>
            <CartContext.Provider value={contextValue}>
                {children}
            </CartContext.Provider>
        </CartRetailerContext.Provider>
    );
};
